import React, { useEffect, useState } from 'react';
import { IProduct, IRating } from '../../interfaces/product';
import { useParams } from 'react-router-dom';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import Layout from '../../components/Layout/Layout';
import { Link } from 'react-router-dom';
import moment from '../../utility/moment-timezone';
import { IModifierOption, IProductModifierGroup } from '../../interfaces/modifier';
import { toFixedThree } from '../../utility/helper';

/* ---------------- Modifier option display resolvers ----------------
 * A linked option keeps the product's own values in `*Override` fields and a
 * standalone option keeps them inline, so overrides always win. Mirrors the
 * resolvers used on the modifier group edit screen.
 */
const optionName = (o: IModifierOption) => o.nameOverride || o.name || o.product?.title || '-';

const optionImage = (o: IModifierOption) => o.imageOverride || o.imageUrl || o.product?.images?.[0];

/** Returns null when no price is set at all — 0 is a real value ("Free"), not a fallback. */
const optionPrice = (o: IModifierOption): number | null => {
    const raw = o.priceOverride ?? o.price;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const optionStock = (o: IModifierOption) => o.inventoryQuantity ?? o.productInfo?.quantity;

const optionWeight = (o: IModifierOption) => o.weight || o.productInfo?.pieceWeight || '-';

/** Group name lives on the nested group; `name` is only present on locally-built entries. */
const groupName = (g: IProductModifierGroup) => g.modifierGroup?.name || g.name || 'Modifier Group';

const ProductDetailsPage: React.FC = () => {
    const [product, setProduct] = useState<IProduct | null>(null);
    const [ratings, setRatings] = useState<IRating[]>([]);
    const [loading, setLoading] = useState(true);
    const { id } = useParams()
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await callApi(
                    API_URL_2 + API_ENDPOINT.page + `products/${id}?all=true`,
                    { method: "GET" }
                );
                setProduct(response);

                const raringResponse = await callApi(
                    API_URL_2 + API_ENDPOINT.page + `products/rating/${id}`,
                    { method: "GET" }
                );
                setRatings(raringResponse.data);
            } catch (err: any) {
                console.log("Error:", err?.message)
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    if (loading) {
        return <Layout ><p>Loading...</p></Layout>;
    }

    if (!product) {
        return <Layout ><p>No product found.</p></Layout>;
    }

    // Calculate average rating (if ratings exist)
    const averageRating =
        ratings.length > 0
            ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length
            : 0;

    // Attached modifier groups, ordered the same way the storefront renders them.
    const modifierGroups = [...(product.modifierGroups || [])].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );



    return (
        <Layout>
            <div className="my-4">


                {/* Product Details Section */}
                <div className="card my-4">
                    <div className="card-header">Product Details</div>
                    <div className="card-body">
                        <div className="row g-3">
                            {/* Product Image Gallery */}
                            <div className="col-12 col-md-5 col-lg-4">
                                {product.images && product.images.length > 0 ? (
                                    <img
                                        src={product.images[0]}
                                        alt={product.title}
                                        className="img-fluid w-100"
                                        style={{ borderRadius: '5px', maxHeight: '320px', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <div>No image available</div>
                                )}
                                {/* You can add a thumbnail gallery here if needed */}
                            </div>
                            {/* Product Info */}
                            <div className="col-12 col-md-7 col-lg-8">
                                <h3 className="d-flex align-items-center flex-wrap gap-2 mb-3">
                                    <span className="text-break">{product.title}</span>

                                    <Link
                                        to={`/edit-product/${product.id}`}
                                        className="btn btn-outline-secondary btn-icon flex-shrink-0"
                                        title="Edit"
                                        role="button"
                                    >
                                        <i className="bi bi-pencil-fill" aria-hidden="true" />
                                    </Link>
                                </h3>


                                <p className="mb-2"><strong>Category:</strong> {product.typeOfMeat[0]}</p>
                                <p className="mb-2"><strong>Cut:</strong> {product.cut}</p>
                                <p className="mb-2"><strong>Brand:</strong> {product.brand}</p>
                                <p className="mb-2"><strong>State:</strong> {product.state}</p>
                                <p className="mb-2">
                                    <strong>Average Rating:</strong>{' '}
                                    <span
                                        className="star-rating"
                                        style={{ color: '#ffc107', fontSize: '1.2rem' }}
                                    >
                                        {'★'.repeat(Math.round(averageRating)) + '☆'.repeat(5 - Math.round(averageRating))}
                                    </span>{' '}
                                    ({ratings.length > 0 ? averageRating.toFixed(1) : 'No ratings'})
                                </p>
                                <p className="mb-2 text-break"><strong>Description:</strong> {product.description}</p>
                                <p className="mb-0 text-break"><strong>Additional Info:</strong> {product.additionalInfo}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Variants Section */}
                <div className="card my-4">
                    <div className="card-header">Product Variants</div>
                    <div className="card-body">
                        {product.productInfo && product.productInfo.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-nowrap">Variant ID</th>
                                            <th className="text-nowrap">Price</th>
                                            <th className="text-nowrap">Quantity</th>
                                            <th className="text-nowrap">Discount %</th>
                                            <th className="text-nowrap">Discounted Price</th>
                                            <th className="text-nowrap">Piece Weight</th>
                                            <th className="text-nowrap">VAT Included</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.productInfo.map((info) => (
                                            <tr key={info.id}>
                                                <td>{info.id}</td>
                                                <td className="text-nowrap">{info.price}</td>
                                                <td className="text-nowrap">{info.quantity}</td>
                                                <td className="text-nowrap">{info.discountPercentage ? `${info.discountPercentage}%` : 'N/A'}</td>
                                                <td className="text-nowrap">{info.discountedPrice ? info.discountedPrice : info.price}</td>
                                                <td className="text-nowrap">{info.pieceWeight}</td>
                                                <td className="text-nowrap">{info.isVatIncluded ? 'Yes' : 'No'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="mb-0">No variants available.</p>
                        )}
                    </div>
                </div>

                {/* Modifier Groups Section — only for products that have customization */}
                {modifierGroups.length > 0 && (
                    <div className="card my-4">
                        <div className="card-header d-flex flex-wrap align-items-center gap-2">
                            <span>Modifier Groups</span>
                            <span className="badge bg-secondary">{modifierGroups.length}</span>
                        </div>
                        <div className="card-body">
                            {modifierGroups.map((group) => {
                                const options = [...(group.modifierGroup?.options || [])].sort(
                                    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
                                );
                                return (
                                    <div
                                        key={group.id || group.modifierGroupId}
                                        className="border rounded p-3 mb-3"
                                    >
                                        {/* Group heading + configuration badges */}
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                            <h5 className="mb-0 text-break">{groupName(group)}</h5>
                                            <div className="d-flex flex-wrap gap-1">
                                                <span
                                                    className={`badge ${group.isRequired ? 'bg-danger' : 'bg-secondary'}`}
                                                >
                                                    {group.isRequired ? 'Required' : 'Optional'}
                                                </span>
                                                {/* <span className="badge bg-info bg-opacity-10 text-info">
                                                    {String(group.selectionType || '').toUpperCase() || 'SINGLE'}
                                                </span> */}
                                                {group.isActive === false && (
                                                    <span className="badge bg-warning">Inactive</span>
                                                )}
                                            </div>
                                        </div>

                                        {group.modifierGroup?.description && (
                                            <p className="text-muted small mb-2 text-break">
                                                {group.modifierGroup.description}
                                            </p>
                                        )}

                                        {/* Selection rules */}
                                        <div className="d-flex flex-wrap gap-3 small mb-3">
                                            <span><strong>Min Selection:</strong> {group.minSelection ?? 0}</span>
                                            <span>
                                                <strong>Max Selection:</strong>{' '}
                                                {group.maxSelection ? group.maxSelection : 'Unlimited'}
                                            </span>
                                            <span><strong>Max Qty Per Option:</strong> {group.maxQtyPerOption ?? 1}</span>
                                            <span><strong>Options:</strong> {options.length}</span>
                                        </div>

                                        {/* Options belonging to this group */}
                                        {options.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-bordered align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-nowrap">Image</th>
                                                            <th className="text-nowrap">Name</th>
                                                            <th className="text-nowrap">Price (BHD)</th>
                                                            <th className="text-nowrap">Weight</th>
                                                            <th className="text-nowrap">Inventory</th>
                                                            <th className="text-nowrap">Type</th>
                                                            <th className="text-nowrap">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {options.map((option) => {
                                                            const price = optionPrice(option);
                                                            const stock = optionStock(option);
                                                            const image = optionImage(option);
                                                            return (
                                                                <tr key={option.id}>
                                                                    <td>
                                                                        {image ? (
                                                                            <img
                                                                                src={image}
                                                                                alt={optionName(option)}
                                                                                style={{
                                                                                    width: 40,
                                                                                    height: 40,
                                                                                    borderRadius: '50%',
                                                                                    objectFit: 'cover',
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <span className="text-muted">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-break">{optionName(option)}</td>
                                                                    <td className="text-nowrap">
                                                                        {price === null ? (
                                                                            '-'
                                                                        ) : price === 0 ? (
                                                                            <span className="badge bg-success">Free</span>
                                                                        ) : (
                                                                            toFixedThree(price)
                                                                        )}
                                                                    </td>
                                                                    <td className="text-nowrap">{optionWeight(option)}</td>
                                                                    <td className="text-nowrap">
                                                                        {stock ?? '-'}
                                                                    </td>
                                                                    <td className="text-nowrap">
                                                                        <span
                                                                            className={`badge ${option.productId ? 'bg-info' : 'bg-secondary'}`}
                                                                        >
                                                                            {option.productId ? 'Product Linked' : 'Standalone'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-nowrap">
                                                                        <span
                                                                            className={`badge ${option.isActive === false ? 'bg-danger' : 'bg-success'}`}
                                                                        >
                                                                            {option.isActive === false ? 'Inactive' : 'Active'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">No options in this group.</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Customer Reviews Section */}
                <div className="card my-4">
                    <div className="card-header">Customer Reviews</div>
                    <div className="card-body">
                        {ratings.length > 0 ? (
                            ratings.map((rating) => (
                                <div key={rating.id} className="mb-4">
                                    <h5 className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                        <span className="text-break">{rating.userName}</span>
                                        <Link to={`/order-details/${rating.orderId}`} className="text-decoration-none">
                                            (View Order)
                                        </Link>
                                        <small className="text-muted">
                                            - {moment(rating.createdAt).format('DD MMM, YYYY')}
                                        </small>
                                    </h5>
                                    <p
                                        className="star-rating mb-2"
                                        style={{ color: '#ffc107', fontSize: '1.2rem' }}
                                    >
                                        {'★'.repeat(Math.round(rating.rating)) +
                                            '☆'.repeat(5 - Math.round(rating.rating))}
                                    </p>
                                    <p className="text-break">{rating.review}</p>
                                    {/* Display rating images if available */}
                                    {rating.imageUrl && rating.imageUrl.length > 0 && (
                                        <div className="d-flex flex-wrap">
                                            {rating.imageUrl.map((url, index) => (
                                                <img
                                                    key={index}
                                                    src={url}
                                                    alt={`Review image ${index + 1}`}
                                                    className="img-thumbnail me-2 mb-2"
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p>No reviews yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ProductDetailsPage;
